import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  HostListener,
  Input,
  OnDestroy
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, Subscription, timer } from 'rxjs';
import { map } from 'rxjs/operators';

import { Env } from 'src/app/core/config/config.model';
import { getEnvSpecificScriptUrl } from 'src/app/core/config/url.config';
import { CURRENT_HOST_ID } from 'src/app/core/host-identification/current-host-id';
import { HostId } from 'src/app/core/host-identification/host-id.enum';
import { LoggerService } from 'src/app/core/services/logger/logger.service';
import { OnlineCodeAndLink, OnlineCodeService } from 'src/app/core/services/online-code/online-code.service';
import { TrackingAction } from 'src/app/core/services/tracking/models/tracking-action.enum';
import { TrackingCategory } from 'src/app/core/services/tracking/models/tracking-category.enum';
import { TrackingEventType } from 'src/app/core/services/tracking/models/tracking-event-type.enum';
import { TrackingLabel } from 'src/app/core/services/tracking/models/tracking-label.enum';
import { createTrackingInput, TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { updateVehicleWishlistUuid } from 'src/app/store/common/common.actions';
import { selectVehicleWishlistUuid } from 'src/app/store/common/common.selectors';
import {
  selectContextVehicleId,
  selectOnlineCodeWithPlaceholderForSaveButton
} from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { ScriptParams } from 'src/app/utils/functions/load-external-script';

import { NotificationService } from '../notification/notification.service';
import { RETURN_TRANSLATION_OR_KEY, translate, TranslatePipe } from '../pipes/translate/translate.pipe';
import { ScriptLoaderComponent } from '../script-loader/script-loader.component';

export const COMPONENT_NAME = 'eqpodc-save-button';

export enum Phase {
  NoCode = 0,
  CodeGenerated = 1,
  ClickTriggered = 2,
  CleanUp = 3
}
@Component({
  selector: 'cc-wishlist-save-button',
  templateUrl: './wishlist-save-button.component.html',
  styleUrl: './wishlist-save-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ScriptLoaderComponent, AsyncPipe, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WishlistSaveButtonComponent implements OnDestroy {
  @Input() public isFullFunctionalButton = false;
  @Input() public isMobile = false;
  @Input() public isFullWidth = false;
  @Input() public showLabelOnMobile = false;
  @Input() public size = 'm';
  @Input() public saveColor = 'primary';
  @Input() public savedColor = 'tertiary';
  @Input() public saveLabel = 'common.save';
  @Input() public savedLabel = 'common.saved';
  public readonly scriptParams: ScriptParams = {
    scriptUrl: getEnvSpecificScriptUrl('wishlist', CURRENT_HOST_ID === HostId.Prod ? Env.Prod : Env.Preprod),
    isModule: true
  };
  public readonly vehicleId$: Observable<string | undefined> = this.store.pipe(selectContextVehicleId);
  public readonly vehicleWishlistUuid$: Observable<string | undefined> = this.store.select(selectVehicleWishlistUuid);
  public readonly directOnlineCode$: Observable<string> = this.store.select(
    selectOnlineCodeWithPlaceholderForSaveButton
  );
  public readonly onlineCode$: Observable<string> = this.onlineCodeService
    .getCurrentConfigUrlAndOnlineCode()
    .pipe(map((onlineCodeAndLink: OnlineCodeAndLink) => onlineCodeAndLink.onlineCode || 'PLACEHOLDER'));
  public scriptLoaded$ = of(false);
  public isDisabled = false;
  public returnTranslationOrKey = RETURN_TRANSLATION_OR_KEY;

  private directOnlineCodeSubscription: Subscription | undefined;
  private phase: Phase = Phase.NoCode;

  public constructor(
    private readonly onlineCodeService: OnlineCodeService,
    private readonly store: Store,
    private readonly notificationService: NotificationService,
    private readonly el: ElementRef<HTMLElement>,
    private readonly trackingService: TrackingService,
    private readonly loggerService: LoggerService
  ) {}

  /**
   * ℹ️ with click on the NBA button we trigger a manual click ({@link NbaBarService}) on
   * the (eqpodc-save-button) widget (which is a child). but that does trigger a button click
   * again and e.g. triggers two tracking events.
   *
   * we don't want that, that's why we stop the event propagation here for programmatically triggered clicks.
   */
  @HostListener('click', ['$event'])
  public preventEventPropagationOnProgrammaticallyTriggeredClick(event: Event): void {
    const isProgrammaticallyTriggeredClick =
      (event.composedPath()[0] as HTMLElement).tagName?.toLowerCase() === COMPONENT_NAME;

    if (isProgrammaticallyTriggeredClick && !this.isFullFunctionalButton) {
      event.stopPropagation();
    }
  }

  /**
   * Raises an error notification if there is a problem when saving the configuration.
   *
   * NOTE: Eror text _can_ be defined within OWCC config with the key 'wishlistSaveButton.onSaveError'.
   * If not available the (localized) error text is taken from the event (wishlist save button component).
   */
  public raiseErrorNotification(event: CustomEvent<{ message: string }>): void {
    const i18nKey = 'wishlistSaveButton.onSaveError';
    this.notificationService.notify({
      content: translate(i18nKey) || event.detail.message,
      variant: 'error',
      dismissable: true,
      autoDismiss: true,
      lifetime: 5000,
      icon: 'alert-circle'
    });
  }

  public ngOnDestroy(): void {
    if (this.directOnlineCodeSubscription) {
      this.directOnlineCodeSubscription.unsubscribe();
    }
  }

  public scriptLoadedHandler(value: boolean): void {
    this.scriptLoaded$ = of(value);
  }

  public onInitializated(event: CustomEvent<{ status: string; uuid: string }[]>): void {
    const [data] = event.detail;
    if (data?.status === 'success') {
      this.saveWishlistUuid(event);
    }
  }

  public onSaved(event: CustomEvent<{ uuid: string }[]>): void {
    this.saveWishlistUuid(event);
  }

  public onUpdated(event: CustomEvent<{ uuid: string }[]>): void {
    this.saveWishlistUuid(event);
  }

  // TODO: cleanup another "temporary" workaround
  // in the future, the save button will no longer require the online code.
  // then we won't need this crutch of creating an onlinecode on the fly any more.
  // this is a workaround for now.
  public onlineCodeCreator(event: MouseEvent): void {
    if (!this.isFullFunctionalButton) {
      return;
    }
    if (this.directOnlineCodeSubscription) {
      this.directOnlineCodeSubscription.unsubscribe();
    }

    if (this.phase === Phase.ClickTriggered) {
      this.trackClick();
      this.phase = Phase.NoCode;
      return;
    }

    this.directOnlineCodeSubscription = this.directOnlineCode$.subscribe(directOnlineCode => {
      if (directOnlineCode === 'PLACEHOLDER') {
        // STEP 1
        // case: first click attempt without valid onlineCode, not saved yet
        this.phase = Phase.NoCode;
        event.stopImmediatePropagation();
        this.isDisabled = true;
        this.onlineCodeService.createOnlineCode();
        this.loggerService.warn(WishlistSaveButtonComponent.name, ['expected phase', this.phase]);
      } else if (directOnlineCode !== 'PLACEHOLDER' && this.phase === Phase.NoCode) {
        // STEP 2
        // case: directOnlinecode has just changed to a valid value but it is not saved yet
        // HINT: alternative for setTimeout, sadly markForCheck and detectChanges are not working regarding data-attribute setting of the wishlistbutton
        // TODO: investigate if this hardcoded delay is really/still necessary
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        timer(10).subscribe(() => {
          this.isDisabled = false;
          const hostElement: HTMLElement | null = this.el.nativeElement.querySelector('eqpodc-save-button');
          hostElement?.click();
        });
        this.phase = Phase.ClickTriggered;
        this.loggerService.warn(WishlistSaveButtonComponent.name, ['expected phase', this.phase]);
      } else {
        this.loggerService.warn(WishlistSaveButtonComponent.name, ['unexpected phase', this.phase]);
      }
    });
  }

  private saveWishlistUuid(event: CustomEvent<{ uuid: string }[]>): void {
    const uuid = event.detail[0]?.uuid;

    if (uuid) {
      this.store.dispatch(
        updateVehicleWishlistUuid({
          wishlistUuid: uuid
        })
      );
    }
  }

  private trackClick(): void {
    this.trackingService.sendTrackingData(
      createTrackingInput(
        TrackingEventType.Click,
        TrackingLabel.NextStepsSave,
        TrackingCategory.Link,
        TrackingAction.SaveConfigurationNbaBar
      )
    );
  }
}
